"use client"

import { useState } from "react"
import { TriangleAlert } from "lucide-react"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Label } from "@/components/ui/label"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { cn } from "@/lib/utils"
import {
  type ConfidenceLevel,
  type Power,
  sampleSizePerVariant,
  significanceTest,
} from "./ab-test-stats"
import {
  CalculatorCard,
  CalculatorGrid,
  FormulaNote,
  NumberField,
  ResultStat,
  parseNum,
} from "./calculator-ui"

/**
 * A/B test significance, two tabs:
 * - Significance: two-proportion z-test on A vs B. Defaults 50/1,000 vs
 *   70/1,000 → z ≈ 1.88, p ≈ 0.060 — just short of 95%, on purpose, so the
 *   first thing a visitor sees is what "not significant yet" looks like.
 * - Sample size: visitors per variant for a baseline rate and relative MDE.
 *   Defaults 5% baseline, +20% MDE, 95%, 80% power → 8,158 per variant.
 */

const VERDICT_TONE = {
  neutral: "border-border bg-muted/40 text-foreground",
  good: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400",
} as const

const CONFIDENCE_LEVELS: ConfidenceLevel[] = [90, 95, 99]
const POWER_LEVELS: Power[] = [80, 90]

function pct(fraction: number, digits = 2): string {
  if (!Number.isFinite(fraction)) return "—"
  return `${(fraction * 100).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`
}

function signedPct(fraction: number, digits = 1): string {
  if (!Number.isFinite(fraction)) return "—"
  const sign = fraction > 0 ? "+" : ""
  return `${sign}${pct(fraction, digits)}`
}

function formatP(p: number): string {
  if (!Number.isFinite(p)) return "—"
  if (p < 0.0001) return "< 0.0001"
  return p.toFixed(4)
}

function formatCount(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString("en-US") : "—"
}

function ChoiceGroup<T extends number>({
  id,
  label,
  options,
  value,
  onChange,
}: {
  id: string
  label: string
  options: T[]
  value: T
  onChange: (next: T) => void
}) {
  return (
    <div className="flex flex-col gap-2">
      <Label id={id} className="text-sm font-medium text-foreground">
        {label}
      </Label>
      <ToggleGroup
        type="single"
        variant="outline"
        aria-labelledby={id}
        value={String(value)}
        onValueChange={(v) => {
          if (v) onChange(Number(v) as T)
        }}
      >
        {options.map((o) => (
          <ToggleGroupItem key={o} value={String(o)} className="px-4">
            {o}%
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  )
}

function VariantFields({
  name,
  label,
  visitors,
  conversions,
  onVisitors,
  onConversions,
}: {
  name: "a" | "b"
  label: string
  visitors: string
  conversions: string
  onVisitors: (v: string) => void
  onConversions: (v: string) => void
}) {
  return (
    <div className="rounded-2xl border border-border p-5">
      <h3 className="mb-4 text-sm font-semibold text-foreground">{label}</h3>
      <div className="flex flex-col gap-4">
        <NumberField
          id={`ab-${name}-visitors`}
          label="Visitors or impressions"
          value={visitors}
          onChange={onVisitors}
          step={1}
        />
        <NumberField
          id={`ab-${name}-conversions`}
          label="Conversions or clicks"
          value={conversions}
          onChange={onConversions}
          step={1}
        />
      </div>
    </div>
  )
}

export function AbTestSignificanceWidget() {
  const [visitorsA, setVisitorsA] = useState("1000")
  const [conversionsA, setConversionsA] = useState("50")
  const [visitorsB, setVisitorsB] = useState("1000")
  const [conversionsB, setConversionsB] = useState("70")
  const [confidence, setConfidence] = useState<ConfidenceLevel>(95)

  const [baseline, setBaseline] = useState("5")
  const [mde, setMde] = useState("20")
  const [sizeConfidence, setSizeConfidence] = useState<ConfidenceLevel>(95)
  const [power, setPower] = useState<Power>(80)

  const result = significanceTest(
    { visitors: parseNum(visitorsA) ?? NaN, conversions: parseNum(conversionsA) ?? NaN },
    { visitors: parseNum(visitorsB) ?? NaN, conversions: parseNum(conversionsB) ?? NaN },
    confidence,
  )

  const verdict = (() => {
    if (!result.ok) return null
    if (!Number.isFinite(result.z)) {
      return {
        tone: "neutral" as const,
        headline: "Nothing to test yet",
        body: "Both variants have the same all-or-nothing rate (every visitor converted, or none did), so there is no variation to compare. Keep the test running.",
      }
    }
    if (result.significant) {
      const winner = result.diff > 0 ? "B" : "A"
      return {
        tone: "good" as const,
        headline: `Significant at ${confidence}% — ${winner} converts better`,
        body: `A difference this large would show up less than ${100 - confidence}% of the time if A and B truly performed the same. Confirm the test ran its planned length before acting on it.`,
      }
    }
    const needed = sampleSizePerVariant(
      result.rateA,
      result.relativeLift,
      confidence,
      80,
    )
    const largest = Math.max(parseNum(visitorsA) ?? 0, parseNum(visitorsB) ?? 0)
    const more =
      Number.isFinite(needed) && needed > largest
        ? ` If the observed lift is real, detecting it reliably (80% power) takes about ${formatCount(needed)} visitors per variant.`
        : ""
    return {
      tone: "warn" as const,
      headline: `Not significant yet at ${confidence}%`,
      body: `The gap between A and B is within what random variation could produce at this sample size. That is not proof the variants perform the same — only that the data cannot tell them apart yet.${more}`,
    }
  })()

  const baselineNum = parseNum(baseline)
  const mdeNum = parseNum(mde)
  const perVariant =
    baselineNum !== null && mdeNum !== null
      ? sampleSizePerVariant(baselineNum / 100, mdeNum / 100, sizeConfidence, power)
      : NaN
  const targetRate =
    baselineNum !== null && mdeNum !== null ? (baselineNum / 100) * (1 + mdeNum / 100) : NaN

  return (
    <CalculatorCard title="A/B Test Significance Calculator">
      <Tabs defaultValue="significance">
        <TabsList className="mb-6">
          <TabsTrigger value="significance">Is it significant?</TabsTrigger>
          <TabsTrigger value="sample-size">Sample size</TabsTrigger>
        </TabsList>

        <TabsContent value="significance">
          <CalculatorGrid>
            <VariantFields
              name="a"
              label="A — control"
              visitors={visitorsA}
              conversions={conversionsA}
              onVisitors={setVisitorsA}
              onConversions={setConversionsA}
            />
            <VariantFields
              name="b"
              label="B — variant"
              visitors={visitorsB}
              conversions={conversionsB}
              onVisitors={setVisitorsB}
              onConversions={setConversionsB}
            />
          </CalculatorGrid>

          <div className="mt-5">
            <ChoiceGroup
              id="ab-confidence"
              label="Confidence level"
              options={CONFIDENCE_LEVELS}
              value={confidence}
              onChange={setConfidence}
            />
          </div>

          {!result.ok ? (
            <Alert variant="destructive" className="mt-6">
              <TriangleAlert />
              <AlertTitle>Check the inputs</AlertTitle>
              <AlertDescription>{result.hint}</AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <ResultStat label="Conversion rate A" value={pct(result.rateA)} />
                <ResultStat
                  label="Conversion rate B"
                  value={pct(result.rateB)}
                  caption={`${signedPct(result.diff, 2)} points vs A.`}
                />
                <ResultStat
                  label="Relative lift (B vs A)"
                  value={signedPct(result.relativeLift)}
                  caption={
                    Number.isFinite(result.relativeLift)
                      ? "How much higher (or lower) B's rate is than A's."
                      : "Undefined while A has zero conversions."
                  }
                />
                <ResultStat
                  label="p-value (two-sided)"
                  value={formatP(result.pValue)}
                  caption={
                    Number.isFinite(result.z)
                      ? `z = ${result.z.toFixed(3)}. Significant at ${confidence}% when p < ${(1 - confidence / 100).toFixed(2)}.`
                      : "No variation to test."
                  }
                  tone={result.significant ? "good" : "neutral"}
                />
              </div>

              <div className="mt-4 rounded-2xl border border-border bg-muted/40 p-6">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {confidence}% confidence interval on B − A
                </span>
                <div className="mt-1 font-mono text-2xl font-bold tracking-tight text-foreground">
                  {signedPct(result.ciLow, 2)} to {signedPct(result.ciHigh, 2)}
                </div>
                <p className="mt-2 text-sm text-muted-foreground">
                  {result.ciLow <= 0 && result.ciHigh >= 0
                    ? "Absolute percentage points. The range includes zero, so “no difference” is still plausible."
                    : "Absolute percentage points. The range excludes zero."}
                </p>
              </div>

              {verdict ? (
                <div
                  className={cn(
                    "mt-4 rounded-2xl border p-6",
                    VERDICT_TONE[verdict.tone],
                  )}
                >
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Verdict
                  </span>
                  <p className="mt-1 text-xl font-semibold tracking-tight">
                    {verdict.headline}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">
                    {verdict.body}
                  </p>
                </div>
              ) : null}

              {result.smallSample ? (
                <Alert className="mt-4">
                  <TriangleAlert />
                  <AlertTitle>Too few events for this test</AlertTitle>
                  <AlertDescription>
                    At least one variant expects fewer than 5 conversions (or
                    non-conversions). The normal approximation behind the z-test is
                    unreliable here — keep collecting data, or use an exact test such as
                    Fisher&rsquo;s.
                  </AlertDescription>
                </Alert>
              ) : null}
            </>
          )}

          <FormulaNote>
            Two-proportion z-test: z = (p<sub>B</sub> − p<sub>A</sub>) ÷ √(p̂(1 − p̂)(1/n
            <sub>A</sub> + 1/n<sub>B</sub>)), where p̂ is the pooled rate. The confidence
            interval uses the unpooled standard error.
          </FormulaNote>
        </TabsContent>

        <TabsContent value="sample-size">
          <CalculatorGrid>
            <NumberField
              id="ab-baseline"
              label="Baseline conversion rate"
              value={baseline}
              onChange={setBaseline}
              suffix="%"
              hint="Control's current rate, from your own data."
            />
            <NumberField
              id="ab-mde"
              label="Minimum detectable effect (relative)"
              value={mde}
              onChange={setMde}
              suffix="%"
              hint="Smallest lift worth detecting — 20 means 5% → 6%."
            />
            <ChoiceGroup
              id="ab-size-confidence"
              label="Confidence level"
              options={CONFIDENCE_LEVELS}
              value={sizeConfidence}
              onChange={setSizeConfidence}
            />
            <ChoiceGroup
              id="ab-power"
              label="Statistical power"
              options={POWER_LEVELS}
              value={power}
              onChange={setPower}
            />
          </CalculatorGrid>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <ResultStat
              label="Visitors per variant"
              value={formatCount(perVariant)}
              caption={
                Number.isFinite(perVariant)
                  ? `Detects a move from ${pct((baselineNum ?? NaN) / 100)} to ${pct(targetRate)}.`
                  : "Enter a baseline between 0% and 100% and a non-zero effect that keeps the target rate below 100%."
              }
            />
            <ResultStat
              label="Total for an A/B test"
              value={formatCount(perVariant * 2)}
              caption="Two variants. Each extra variant adds another full share."
            />
          </div>
          <FormulaNote>
            n per variant = [z<sub>1−α/2</sub>√(2p̄(1 − p̄)) + z<sub>power</sub>√(p₁(1 −
            p₁) + p₂(1 − p₂))]² ÷ (p₂ − p₁)², with p₂ = p₁ × (1 + MDE) and p̄ the average
            of the two.
          </FormulaNote>
        </TabsContent>
      </Tabs>
    </CalculatorCard>
  )
}
